import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ExternalLink, Plus, Receipt, Trash2 } from 'lucide-react';
import { Button } from '@/components/button';
import { Card, CardBody } from '@/components/card';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { EmptyState } from '@/components/empty-state';
import { Menu } from '@/components/menu';
import { Pager } from '@/components/pager';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  invoiceDeleteWarning,
  useAllInvoices,
  useDeleteAnyInvoice,
  useInvoicesSummary,
} from '@/lib/invoices';
import { formatDateLong, formatMoney } from '@/lib/format';
import { PageHeader } from '@/components/page-header';
import { PickClientDialog } from '@/components/pick-client-dialog';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { StatusPill } from '@/components/status-dot';
import { cn } from '@/lib/cn';
import type { InvoiceData } from '@/types/api';

function statusTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' {
  if (status === 'paid') return 'success';
  if (status === 'partial') return 'warning';
  if (status === 'sent') return 'info';
  return 'neutral';
}

function SummaryTiles() {
  const { data } = useInvoicesSummary();
  const outstanding = data?.outstanding_cents ?? 0;
  const overdue = data?.overdue_cents ?? 0;

  return (
    <div className="grid grid-cols-2 gap-3 sm:max-w-md">
      <Card>
        <CardBody className="py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--color-foreground-subtle)]">
            Outstanding
          </div>
          <div className="mt-1 text-lg font-semibold tabular-nums">
            {formatMoney(outstanding, 'USD')}
          </div>
          <div className="text-xs text-muted-foreground">{data?.open_count ?? 0} open</div>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--color-foreground-subtle)]">
            Overdue
          </div>
          <div
            className={cn(
              'mt-1 text-lg font-semibold tabular-nums',
              overdue > 0 ? 'text-[var(--color-danger)]' : '',
            )}
          >
            {formatMoney(overdue, 'USD')}
          </div>
          <div className="text-xs text-muted-foreground">
            {data?.overdue_count ?? 0} {data?.overdue_count === 1 ? 'invoice' : 'invoices'}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/** Filters a link may open the list on, e.g. the dashboard's Open AR. */
const STATUS_FILTERS = ['open', 'overdue', 'draft', 'sent', 'partial', 'paid', 'void'];

export function InvoicesIndex() {
  const [searchParams] = useSearchParams();
  const linkedStatus = searchParams.get('status');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(
    linkedStatus && STATUS_FILTERS.includes(linkedStatus) ? linkedStatus : '',
  );
  const [page, setPage] = useState(1);
  const [pickOpen, setPickOpen] = useState(false);
  const navigate = useNavigate();
  const { data: paginated, isLoading } = useAllInvoices({
    q: q || undefined,
    status: status || undefined,
    page,
  });
  const items = paginated?.data;
  const meta = paginated?.meta;
  const del = useDeleteAnyInvoice();
  const confirm = useConfirm();
  const toast = useToast();

  /**
   * Deletable at any status, paid included. Voiding leaves the invoice on the
   * books; deleting is for invoices that shouldn't exist at all.
   */
  async function deleteInvoice(invoice: InvoiceData) {
    const ok = await confirm({
      title: `Delete ${invoice.number}?`,
      body: invoiceDeleteWarning(invoice),
      confirmLabel: 'Delete invoice',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await del.mutateAsync({ clientId: invoice.client_id, id: invoice.id });
      toast.success(`${invoice.number} deleted`);
    } catch (err) {
      toast.error('Something went wrong', err instanceof Error ? err.message : undefined);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Receipt}
        title="Invoices"
        subtitle="Every invoice across all clients."
        action={
          <Button onClick={() => setPickOpen(true)}>
            <Plus className="size-4" /> New invoice
          </Button>
        }
      />

      <SummaryTiles />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by invoice number…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="max-w-[180px]"
        >
          <option value="">All statuses</option>
          <option value="open">Open (owed)</option>
          <option value="overdue">Overdue</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="partial">Partially paid</option>
          <option value="paid">Paid</option>
          <option value="void">Void</option>
        </Select>
      </div>

      {isLoading ? (
        <SkeletonList rows={5} />
      ) : !items || items.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No invoices here yet"
          description="Pick a client to create the first invoice."
          action={
            <Button onClick={() => setPickOpen(true)}>
              <Plus className="size-4" /> New invoice
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          <DataList>
            {items.map((inv) => (
              <DataListRow
                key={inv.id}
                onClick={() => navigate(`/clients/${inv.client_id}?tab=invoices`)}
                interactive
              >
                <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-emerald-500/12 text-emerald-700 dark:text-emerald-300">
                  <Receipt className="size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate font-medium text-foreground">{inv.number}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {inv.status_label}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <Link
                      to={`/clients/${inv.client_id}`}
                      className="hover:text-foreground hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {inv.client_name ?? `Client #${inv.client_id}`}
                    </Link>
                    {inv.due_date ? (
                      <>
                        <span aria-hidden>·</span>
                        <span className={inv.is_overdue ? 'text-[var(--color-danger)]' : ''}>
                          due {formatDateLong(inv.due_date)}
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="hidden text-right md:block">
                  <div className="text-sm font-medium tabular-nums text-foreground">
                    {formatMoney(inv.total_cents, inv.currency)}
                  </div>
                  {inv.balance_cents > 0 && inv.balance_cents !== inv.total_cents ? (
                    <div className="text-xs tabular-nums text-muted-foreground">
                      {formatMoney(inv.balance_cents, inv.currency)} due
                    </div>
                  ) : null}
                </div>
                {inv.is_overdue ? (
                  <StatusPill tone="danger">Overdue</StatusPill>
                ) : (
                  <StatusPill tone={statusTone(inv.status)}>{inv.status_label}</StatusPill>
                )}
                {inv.public_url ? (
                  <a
                    href={inv.public_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Open ${inv.number} pay page in a new tab`}
                    className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover/row:opacity-100"
                  >
                    <ExternalLink className="size-4" />
                  </a>
                ) : null}
                <div onClick={(e) => e.stopPropagation()}>
                  <Menu
                    triggerLabel={`Actions for ${inv.number}`}
                    items={[
                      {
                        key: 'delete',
                        label: 'Delete',
                        icon: Trash2,
                        danger: true,
                        onSelect: () => void deleteInvoice(inv),
                      },
                    ]}
                  />
                </div>
              </DataListRow>
            ))}
          </DataList>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}

      <PickClientDialog
        open={pickOpen}
        onClose={() => setPickOpen(false)}
        title="New invoice"
        tab="invoices"
      />
    </div>
  );
}
